import React, { useState } from 'react'
import DateHelpers from '../Helper/DateHelpers'
import '../Styles/CartItemCard.scss'

// One registration waiting to be paid for.
//
// Every figure shown here arrives already calculated from /cart/quote. This component does no
// arithmetic of its own — that is the rule that keeps the browser from ever deciding a price.
function CartItemCard({ line, onToggleSelected, onChangeOption, onApplyPromo, onRemovePromo, onRemove, busy }) {

   const [promoInput, setPromoInput] = useState('')
   const [promoError, setPromoError] = useState('')
   const [showDetails, setShowDetails] = useState(false)

   const asDollars = (cents) => (cents / 100).toFixed(2)

   async function applyPromo(event) {
      event.preventDefault()
      setPromoError('')
      const message = await onApplyPromo(line.cart_item_id, promoInput)
      if (message) {
         setPromoError(message)
      } else {
         setPromoInput('')
      }
   }

   return (
      <div className={`CartItemCardContainer ${line.unavailable ? 'CartItemCardUnavailable' : ''}`}>

         <div className="CartItemCardCheckbox">
            <input
               type="checkbox"
               checked={line.selected}
               disabled={line.unavailable || busy}
               onChange={() => onToggleSelected(line.cart_item_id)}
               aria-label={`Include ${line.first_name} ${line.last_name} in this payment`} />
         </div>

         <div className="CartItemCardMain">

            <div className="CartItemCardHeader">
               <div className="CartItemCardHeading">
                  <div className="CartItemCardEventName">{line.camp_name}</div>
                  <div className="CartItemCardDates">
                     {DateHelpers.getEventDateRange(line.begin_date, line.end_date)}
                  </div>
                  <div className="CartItemCardCamperName">
                     Camper: {line.first_name} {line.last_name}
                     {line.attendees > 1 && ` (+${line.attendees - 1} son${line.attendees > 2 ? 's' : ''})`}
                  </div>
                  {line.group_name && (
                     <div className="CartItemCardCamperName">
                        Attending With: {line.group_name}
                     </div>
                  )}
               </div>
               <div className="CartItemCardHeaderActions">
                  <button type="button" className="CartItemCardLinkButton" onClick={() => setShowDetails(!showDetails)}>
                     {showDetails ? 'Hide details' : 'View details'}
                  </button>
                  <button type="button" className="CartItemCardLinkButton" disabled={busy}
                     onClick={() => onRemove(line.cart_item_id)}>
                     Remove
                  </button>
               </div>
            </div>

            {line.unavailable && (
               <div className="CartItemCardUnavailableNotice">{line.unavailable_reason}</div>
            )}

            {line.price_changed && !line.unavailable && (
               <div className="CartItemCardPriceChanged">
                  The price of this week changed from ${asDollars(line.snapshot_full_cents)} to
                  {' '}${asDollars(line.full_cents)} since you added it to your cart.
               </div>
            )}

            {line.promo_dropped && (
               <div className="CartItemCardPromoDropped">
                  {line.promo_code} is no longer available and has been removed.
                  This registration is now ${asDollars(line.full_cents)}.
               </div>
            )}

            {!line.unavailable && (
               <>
                  <div className="CartItemCardFees">
                     <div className="CartItemCardFeeRow">
                        <div>Registration fee{line.attendees > 1 && ` (${line.attendees} people)`}</div>
                        <div>${asDollars(line.registration_fee_cents)}</div>
                     </div>
                     <div className="CartItemCardFeeRow">
                        <div>Camp fee{line.attendees > 1 && ` (${line.attendees} people)`}</div>
                        <div>${asDollars(line.camp_fee_cents)}</div>
                     </div>
                     {line.discount_cents > 0 && (
                        <div className="CartItemCardFeeRow CartItemCardDiscount">
                           <div>Promo {line.promo_code}</div>
                           <div>−${asDollars(line.discount_cents)}</div>
                        </div>
                     )}
                     <div className="CartItemCardFeeRow CartItemCardFeeTotal">
                        <div>Registration total</div>
                        <div>${asDollars(line.full_cents - line.discount_cents)}</div>
                     </div>
                  </div>

                  <div className="CartItemCardPromo">
                     {line.promo_code && !line.promo_dropped ? (
                        <div className="CartItemCardPromoApplied">
                           <span className="CartItemCardPromoTag">✓ {line.promo_code} applied</span>
                           <button type="button" disabled={busy}
                              onClick={() => onRemovePromo(line.cart_item_id)}>
                              Remove code
                           </button>
                        </div>
                     ) : (
                        <form className="CartItemCardPromoForm" onSubmit={applyPromo}>
                           <input
                              type="text"
                              placeholder="Promo code"
                              value={promoInput}
                              disabled={busy}
                              onChange={(e) => setPromoInput(e.target.value)} />
                           <button type="submit" disabled={busy || !promoInput.trim()}>Apply</button>
                        </form>
                     )}
                     {promoError && <div className="CartItemCardPromoError">{promoError}</div>}
                  </div>

                  {/* A line covered entirely by a promo has nothing left to choose between. */}
                  {line.option_entire_cents > 0 && (
                     <div className="CartItemCardOptions">
                        <label className={line.payment_option === 'entire' ? 'CartItemCardOptionSelected' : ''}>
                           <div className="CartItemCardOptionRow">
                              <input
                                 type="radio"
                                 name={`option-${line.cart_item_id}`}
                                 checked={line.payment_option === 'entire'}
                                 disabled={busy}
                                 onChange={() => onChangeOption(line.cart_item_id, 'entire')} />
                              <span>Pay entire amount</span>
                           </div>
                           <span className="CartItemCardOptionAmount">${asDollars(line.option_entire_cents)}</span>
                        </label>
                        <label className={line.payment_option === 'reg_fee' ? 'CartItemCardOptionSelected' : ''}>
                           <div className="CartItemCardOptionRow">
                              <input
                                 type="radio"
                                 name={`option-${line.cart_item_id}`}
                                 checked={line.payment_option === 'reg_fee'}
                                 disabled={busy}
                                 onChange={() => onChangeOption(line.cart_item_id, 'reg_fee')} />
                              <span>Pay registration fee only</span>
                           </div>
                           <span className="CartItemCardOptionAmount">
                              ${asDollars(line.option_reg_fee_cents)} now
                              {line.full_cents - line.discount_cents - line.option_reg_fee_cents > 0 &&
                                 ` · $${asDollars(line.full_cents - line.discount_cents - line.option_reg_fee_cents)} later`}
                           </span>
                        </label>
                     </div>
                  )}
               </>
            )}

            {showDetails && (
               <div className="CartItemCardDetails">
                  <div><strong>Registration type:</strong> {line.camp_type || line.regist_flow}</div>
                  <div><strong>Camper:</strong> {line.first_name} {line.last_name}</div>
                  <div><strong>Event:</strong> {line.camp_name}</div>
                  <div><strong>Dates:</strong> {DateHelpers.getEventDateRange(line.begin_date, line.end_date)}</div>
                  <p className="CartItemCardDetailsNote">
                     To change any of the information on this registration, remove it and register again.
                  </p>
               </div>
            )}

         </div>
      </div>
   )
}

export default CartItemCard
